import React from 'react';
import { useLocation } from '@reach/router';
import * as Ink from '@carta/ink';
import { useAnalyticsV2 } from '@carta/fep-analytics';

import pj from '../../../package.json';

import SideNav from '../Common/SideNav/SideNav';
import { StyledSideNavLogoWrapper } from '../Common/SideNav/styles';
import Logo from '../Common/Logo';
import DocsContext from '../../contexts/DocsContext';

import {
  NAV_SECTIONS,
  useExpandedNavSections,
  isRouteActive,
  NavItem,
  NavSection,
  isNavNestedSection,
  navBubble,
} from './index';
import { SideNavSection } from './components/SideNavSection';
import { SideNavNestedSection } from './components/SideNavNestedSection';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';

const SideNavigation = React.memo(() => {
  const location = useLocation();
  const { trackAction } = useAnalyticsV2();
  const { setIsPreferencesModalOpen } = React.useContext(DocsContext);
  const { openSections, toggleSection } = useExpandedNavSections(location.pathname, NAV_SECTIONS);
  const isMobileLayout = useIsMobileLayout();

  // Hide sidenav on smaller screens - dropdown is rendered in Layout
  if (isMobileLayout) {
    return null;
  }

  return (
    <SideNav width="272px">
      <StyledSideNavLogoWrapper>
        <Logo />
      </StyledSideNavLogoWrapper>
      <SideNav.ContentContainer>
        <SideNav.Group>
          {NAV_SECTIONS.map((item, index) => {
            // Divider
            if ('type' in item) {
              return <SideNav.Divider key={index} />;
            }

            // Default NavItem
            if ('label' in item) {
              const navItem = item as NavItem;

              // Special handling for preferences item
              if (navItem.id === 'preferences') {
                return (
                  <SideNav.Item
                    key={index}
                    title={navItem.label}
                    url={navItem.route[0]}
                    isActive={isRouteActive(location.pathname, navItem.route)}
                    withReactRouter={false}
                    onClick={e => {
                      e.preventDefault();
                      trackAction({ elementId: `sidebar-link-${navItem.id}`, type: 'click' });
                      setIsPreferencesModalOpen(true);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        trackAction({ elementId: `sidebar-link-${navItem.id}`, type: 'click' });
                        setIsPreferencesModalOpen(true);
                      }
                    }}
                  />
                );
              }

              return (
                <SideNav.Item
                  key={index}
                  title={navItem.label}
                  url={navItem.route[0]}
                  isActive={isRouteActive(location.pathname, navItem.route)}
                  openInNewTab={navItem.target === '_blank'}
                  withReactRouter={!navItem.target}
                  bubble={navBubble(navItem.bubble)}
                  onClick={() => trackAction({ elementId: `sidebar-link-${navItem.id}`, type: 'click' })}
                />
              );
            }

            // At this point, item is a NavSection
            const navSection = item as NavSection;
            const isOpen = openSections.has(String(index));
            const hasNestedSections = navSection.children.some(child => isNavNestedSection(child));

            if (hasNestedSections) {
              return (
                <SideNavNestedSection
                  key={index}
                  section={navSection}
                  sectionIndex={index}
                  pathname={location.pathname}
                  isOpen={isOpen}
                  openSections={openSections}
                  toggleSection={toggleSection}
                  trackAction={trackAction}
                />
              );
            }

            return (
              <SideNavSection
                key={index}
                section={navSection}
                sectionIndex={index}
                pathname={location.pathname}
                isOpen={isOpen}
                toggleSection={toggleSection}
                trackAction={trackAction}
              />
            );
          })}
        </SideNav.Group>
        <Ink.Box left="16px" right="16px" vertical="10px">
          <Ink.CustomText size="xsmall" weight="light" variant="feedback-neutral">
            Version {pj.dependencies['@carta/ink']}
          </Ink.CustomText>
        </Ink.Box>
      </SideNav.ContentContainer>
    </SideNav>
  );
});

SideNavigation.displayName = 'SideNavigation';

export default SideNavigation;
